import React, { FunctionComponent, memo, useMemo } from "react";
import { Box, Pagination as MuiPagination, PaginationItem } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { PaginationProps } from "@shared/components/Pagination/models";

const STROKE_COLOR = "#093549";
const HOVER_BACKGROUND = "#EBEBEB";
const MOBILE_BREAKPOINT = "@media (max-width: 768px)";

const PreviousLabel: FunctionComponent = () => (
  <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
    <Box component="span" aria-hidden="true">
      &lsaquo;
    </Box>
    <Box component="span">Previous</Box>
  </Box>
);

const NextLabel: FunctionComponent = () => (
  <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
    <Box component="span">Next</Box>
    <Box component="span" aria-hidden="true">
      &rsaquo;
    </Box>
  </Box>
);

const useStyles = makeStyles(() => ({
  container: {
    marginTop: 30,
    minHeight: 60,
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    padding: "12px 20px",
    borderTop: `2px solid ${STROKE_COLOR}`,
    backgroundColor: "#FFFFFF",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    rowGap: 12,
    columnGap: 16,
    overflow: "hidden",
    [MOBILE_BREAKPOINT]: {
      justifyContent: "center",
    },
  },
  label: {
    flex: "1 1 auto",
    minWidth: 0,
    fontSize: 14,
    color: STROKE_COLOR,
    textAlign: "left",
    overflowWrap: "break-word",
    [MOBILE_BREAKPOINT]: {
      flex: "0 0 auto",
      width: "100%",
      textAlign: "center",
    },
  },
  pagination: {
    flex: "0 1 auto",
    minWidth: 0,
    maxWidth: "100%",
    "& .MuiPagination-ul": {
      gap: "4px",
      flexWrap: "wrap",
      justifyContent: "flex-end",
    },
    [MOBILE_BREAKPOINT]: {
      width: "100%",
      "& .MuiPagination-ul": {
        justifyContent: "center",
      },
    },
  },
  item: {
    minWidth: 36,
    width: 36,
    height: 36,
    margin: 0,
    padding: 0,
    borderRadius: 8,
    fontSize: 14,
    color: STROKE_COLOR,
    border: "1px solid transparent",
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: HOVER_BACKGROUND,
    },
    "&.Mui-selected": {
      backgroundColor: "transparent",
      border: `1px solid ${STROKE_COLOR}`,
      "&:hover": {
        backgroundColor: "transparent",
      },
    },
    "&.Mui-disabled": {
      opacity: 0.4,
    },
  },
  ellipsisItem: {
    width: 36,
    height: 36,
    fontSize: 14,
    lineHeight: "36px",
    textAlign: "center",
    color: STROKE_COLOR,
    transform: "translateY(-2px)",
  },
  navItem: {
    minWidth: "auto",
    width: "auto",
    padding: "0 10px",
    whiteSpace: "nowrap",
  },
}));

const Pagination: FunctionComponent<PaginationProps> = ({
  count,
  page,
  rowsPerPage,
  onPageChange,
  recordLabel = "Contact Records",
  className,
  testId,
}) => {
  const classes = useStyles();

  const totalPages = useMemo(() => Math.ceil(count / rowsPerPage), [count, rowsPerPage]);

  // AC1 — Hide the entire pagination section when total records are at or below the page size.
  if (count <= rowsPerPage) {
    return null;
  }

  const fromIndex = (page - 1) * rowsPerPage + 1;
  const toIndex = Math.min(page * rowsPerPage, count);

  const handleChange = (_event: React.ChangeEvent<unknown>, newPage: number) => {
    onPageChange(newPage);
  };

  return (
    <Box className={`${classes.container}${className ? ` ${className}` : ""}`} data-testid={testId ?? "pagination"}>
      <Box className={classes.label} data-testid="pagination-label">
        {`Showing ${fromIndex}-${toIndex} of ${count} ${recordLabel}`}
      </Box>
      <MuiPagination
        className={classes.pagination}
        count={totalPages}
        page={page}
        onChange={handleChange}
        siblingCount={0}
        boundaryCount={1}
        shape="rounded"
        renderItem={(item) => {
          if (item.type === "previous") {
            return (
              <PaginationItem
                {...item}
                className={`${classes.item} ${classes.navItem}`}
                components={{ previous: PreviousLabel }}
              />
            );
          }

          if (item.type === "next") {
            return (
              <PaginationItem
                {...item}
                className={`${classes.item} ${classes.navItem}`}
                components={{ next: NextLabel }}
              />
            );
          }

          if (item.type === "start-ellipsis" || item.type === "end-ellipsis") {
            return (
              <Box component="div" className={classes.ellipsisItem}>
                …
              </Box>
            );
          }

          return <PaginationItem {...item} className={classes.item} />;
        }}
      />
    </Box>
  );
};

export default memo<PaginationProps>(Pagination);
